import { formatDeliveryAddress, formatPointAddress } from '@shared/lib'
import type { SelectedDeliveryAddress, UserAddress } from '~/utils/types/user'

/**
 * Единый формат выбранного адреса доставки: как бы адрес ни попал в выбор —
 * из «Моих адресов», из стартовой шторки, из окна карты, — строка одна и та же:
 * «населённый пункт, улица, дом», а подъезд и квартира едут отдельными полями и
 * добавляются в строку заказа (formatDeliveryAddress).
 */

const clean = (value: string | number | null | undefined): string =>
  value == null ? '' : String(value).trim()

/** zona из API приходит строкой («1», «», null) — в выбранном адресе это число или null. */
export function parseZona(zona: string | number | null | undefined): number | null {
  const raw = clean(zona)
  if (raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Каноническая точка сохранённого адреса. Пересобираем из компонентов геокодера
 * (city/street/building) только когда они согласованы со строкой: у адресов,
 * записанных старым окном карты, компоненты могли остаться от филиала («Широкая
 * улица, 32» при строке «Г. Боровичи…»), а у части — быть пустыми. Признак
 * согласованности простой: номер дома из компонентов встречается в строке.
 */
export function canonicalSavedAddress(addr: Pick<UserAddress, 'address' | 'city' | 'street' | 'building'>): string {
  const address = clean(addr.address)
  const city = clean(addr.city)
  const building = clean(addr.building)
  if (!city || !building) return address
  if (!address.toLowerCase().includes(building.toLowerCase())) return address
  return formatPointAddress({ locality: city, street: addr.street, house: building })
}

/** Выбранный адрес доставки из записи «Моих адресов» — со всеми уточнениями. */
export function toSelectedDeliveryAddress(addr: UserAddress): SelectedDeliveryAddress {
  return {
    address: canonicalSavedAddress(addr),
    zona: parseZona(addr.zona),
    latitude: addr.latitude ?? null,
    longitude: addr.longitude ?? null,
    entrance: addr.entrance ?? null,
    apartment: addr.apartment ?? null,
    floor: addr.floor == null ? null : String(addr.floor),
    intercom: addr.intercom ?? null,
    comment: addr.comment ?? null,
  }
}

/**
 * Строка адреса для заказа и витрины: точка + «подъезд N, кв. M».
 * @param point точка адреса; по умолчанию — из выбранного адреса. Форма оформления
 * может держать более свежую строку, чем профиль, — тогда её передают явно.
 */
export function formatSelectedDeliveryAddress(
  selected: SelectedDeliveryAddress | null | undefined,
  point: string | null | undefined = selected?.address,
): string {
  return formatDeliveryAddress(point, selected)
}
